import { checkGaInit } from '../init/ga';
import { createRuntimeGoogleState } from '../init/ga/state';
import { checkECommerceDataError } from './error';
import { handleProducts } from './products';
import { trackTMSConfirmation } from './tmsConfirmation';
import {
  trackConfirmation,
  trackECommerceData,
  trackECommerceUserProperties,
} from './tracking';
import type { ECommerce4Transaction, TrackECommerceArg } from './types';
import { getEcommerceData } from './utils';

/**
 * This function is responsible for tracking eCommerce data. It initializes necessary repositories and clients,
 * checks Google Analytics initialization, sets parameters, and tracks user properties. It also handles products
 * if they exist in the eCommerce data.
 *
 * Ad-hoc transactions (e.g. `purchase-modification`, `itinerary-modification`) are supported via the optional
 * `event_name` field on `ECommerce4Transaction` (GA4-shaped payload only). When omitted/empty, behavior is
 * unchanged (`purchase` GA4 event + `confirmation` GA4/TMS page). When provided, it drives both the GA4 event
 * name (passed as-is to `gtag`; the library never transforms consumer-supplied data — a non-blocking error is
 * logged via `trackError` if it doesn't match GA4's naming requirements, `[A-Za-z][A-Za-z0-9_]{0,39}`) and the
 * `confirmation-${event_name}` convention used for the GA4 virtual pageview and the TMS `env_template`/`screen_name`.
 * See PEX-3715/PEX-4129.
 *
 * An optional `screen_name` can also be provided: when set, it replaces the calculated confirmation page name
 * (GA4 virtual pageview and TMS `page_name`).
 *
 * @async
 * @function
 * @param {TrackECommerceArg | ECommerce4Transaction} eCommerceData - The eCommerce data to be tracked. This can be of type TrackECommerceArg or ECommerce4Transaction. Defaults to an empty object.
 * @returns {Promise<void>} Returns a promise that resolves when all tracking and handling operations are complete.
 */
export const trackECommerce = async (
  eCommerceData: TrackECommerceArg | ECommerce4Transaction
): Promise<void> => {
  if (checkECommerceDataError(eCommerceData)) {
    return;
  }
  await checkGaInit();
  const { setParameters } = createRuntimeGoogleState();
  setParameters({ hasThePageBeenTracked: false });
  trackECommerceUserProperties(eCommerceData);
  trackECommerceData(eCommerceData);
  handleProducts(eCommerceData);
  const {
    transaction_id: transactionId,
    value: revenue,
    event_name: eventNameArg,
  } = getEcommerceData(eCommerceData)?.ga4Data || {};
  const eventName = eventNameArg || 'purchase';
  const { screen_name } = eCommerceData as ECommerce4Transaction;
  await trackConfirmation(eventName, screen_name);
  await trackTMSConfirmation({
    transactionId: transactionId ?? '',
    revenue: revenue ?? 0,
    eventName,
    screenName: screen_name,
  });
};
